//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️
//CRN_20260512 - Server-side encryption requires SecurityServerSideEncryption & SecurityServerSideEncryptionDashboard

import { ConfigSystemParams, Duration, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from '../system/config-system/config-system-service';
import { DashboardCostsPerZoneDTO } from 'src/app/core/models/system/dashboard/dashboard-costs-per-zone.dto';
import { DashboardTotal_Previous_Current_PercentDTO } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { DashboardTotalLicencesLiveButEndedDTO } from 'src/app/core/models/system/dashboard/dashboard-total-licences-live-but-ended.dto';
import { DashboardUsersWithMostCostDTO } from 'src/app/core/models/system/dashboard/dashboard-users-with-most-cost.dto';
import { EncryptionService } from './encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from './exceptions-service';
import { HttpClientService } from './http-client-service';
import { Injectable } from '@angular/core';
import { interval, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class DashboardService {

  private encryptedCompanyIdCache: Map<number, { token: Observable<StringResultDTO>; expiresAt: number }>=new Map();

  constructor(
    private configSystemService: ConfigSystemService,
    private encryptionService  : EncryptionService,
    private exceptionsService  : ExceptionsService,
    private httpClientService  : HttpClientService
  ) { }

  private getEncryptedCompanyId(companyID: number): Observable<StringResultDTO> {
    try {
      const cached=this.encryptedCompanyIdCache.get(companyID);

      if (cached==undefined || Date.now() >= cached.expiresAt) {
        const encrypted$=this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyID, this.encryptionService.serverSideEncryptionDashboard)
                         .pipe(shareReplay(1));

        this.encryptedCompanyIdCache.set(companyID, { token: encrypted$, expiresAt: Date.now() + Duration._4MinMilli });

        return encrypted$;
      }

      return cached.token;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getAlertsTimesTriggeredCountry(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetAlertsTimesTriggeredCountry +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getAlertsTimesTriggeredCountryUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableAlertsTimesTriggeredCountryMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getAlertsTimesTriggeredCountry(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getAlertsTimesTriggeredUsage(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetAlertsTimesTriggeredUsage +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getAlertsTimesTriggeredUsageUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableAlertsTimesTriggeredUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getAlertsTimesTriggeredUsage(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getAlertsTotalLimitIncreases(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetAlertsTotalLimitIncreases +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getAlertsTotalLimitIncreasesUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableAlertsTotalLimitIncreasesMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getAlertsTotalLimitIncreases(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getAlertsTotalThrottles(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetAlertsTotalThrottles +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getAlertsTotalThrottlesUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableAlertsTotalThrottlesMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getAlertsTotalThrottles(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCostsPerZone(companyID: number, year: number, month: number, day: number): Observable<DashboardCostsPerZoneDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardCostsPerZoneDTO[]>(environment.eSimApi +
                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                          eSimApiPaths.DashboardGetCostsPerZone +
                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                          "&year=" + year +
                                                                                          "&month=" + month +
                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
   
  public getTotalCompaniesCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalCompaniesCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalCompaniesCountUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalCompaniesCountMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalCompaniesCount(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalCostsUsage(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalCosts +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalCostsUsageUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalCostsUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalCostsUsage(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalCostsUsageAndLicence(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalCostsUsageAndLicence +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalCostsUsageAndLicenceUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalCostsUsageAndLicenceMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalCostsUsageAndLicence(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalAvailableeSimCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Webbing + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalAvailableeSimCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalAvailableSimCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Webbing + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalAvailableSimCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalCostsLicence(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalLicenceCosts +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalCostsLicenceUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalCostsLicenceMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalCostsLicence(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalPlatformLicencesCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalPlatformLicenceCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalPlatformLicencesCountUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalPlatformLicencesCountMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalPlatformLicencesCount(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalPlatformLicencesLiveButEnded(companyID: number): Observable<DashboardTotalLicencesLiveButEndedDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotalLicencesLiveButEndedDTO>(environment.eSimApi +
                                                                                                     eSimApiPaths.Dashboard + "/" +
                                                                                                     eSimApiPaths.DashboardGetTotalPlatformLicencesLiveButEnded +
                                                                                                     "?companyIdEnc=" + companyIdEnc); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalPlatformLicencesLiveButEndedUpdates(companyID: number): Observable<DashboardTotalLicencesLiveButEndedDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalPlatformLicencesLiveButEndedMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalPlatformLicencesLiveButEnded(companyID))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalPlatformLicencesUnused(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalPlatformLicencesUnused +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalPlatformLicencesUnusedUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalPlatformLicencesUnusedMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalPlatformLicencesUnused(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalProfitUsage(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalCostsProfit +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalProfitUsageUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalProfitUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalProfitUsage(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalPurchaseCostsUsage(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalCostsPurchase +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalPurchaseCostsUsageUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalPurchaseCostsUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalPurchaseCostsUsage(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalReservedeSimCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Webbing + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalReservedeSimCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalReservedSimCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Webbing + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalReservedSimCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalSimCount(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalSimCount +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalSimCountUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalSimCountMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalSimCount(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getTotalSimProfit(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalSimProfit +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalSimProfitUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalSimProfitMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalSimProfit(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalSimProfitLicenceCosts(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalSimProfitLicenceCosts +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalSimProfitLicenceCostsUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalSimProfitMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalSimProfitLicenceCosts(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalSimProfitPurchaseCosts(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalSimProfitPurchaseCosts +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalSimProfitPurchaseCostsUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalSimProfitMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalSimProfitPurchaseCosts(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalUsage(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO>(environment.eSimApi +
                                                                                                          eSimApiPaths.Dashboard + "/" +
                                                                                                          eSimApiPaths.DashboardGetTotalUsage +
                                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                                          "&year=" + year +
                                                                                                          "&month=" + month +
                                                                                                          "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getTotalUsageUpdates(companyID: number, year: number, month: number, day: number): Observable<DashboardTotal_Previous_Current_PercentDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getTotalUsage(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getUserWithMostCost(companyID: number, year: number, month: number, day: number): Observable<DashboardUsersWithMostCostDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DashboardUsersWithMostCostDTO[]>(environment.eSimApi +
                                                                                               eSimApiPaths.Dashboard + "/" +
                                                                                               eSimApiPaths.DashboardGetUserWithMostCost +
                                                                                               "?companyIdEnc=" + companyIdEnc +
                                                                                               "&year=" + year +
                                                                                               "&month=" + month +
                                                                                               "&day=" + day); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
